/**
 * Receipt-date checks at confirm.
 *
 * "Not confident" is not a model score. confidence.receipt_date stays a
 * review highlight only. The gate uses two facts from the document:
 *   - the printed day name, or its absence
 *   - whether a month name is printed (Oct) or the date is digits only (22.10.2026)
 * plus the calendar: a future date, and a date more than twelve months before capture.
 */

export const PRINTED_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type PrintedWeekday = (typeof PRINTED_WEEKDAYS)[number];

const WEEKDAY_ALIASES: Record<string, PrintedWeekday> = {
  sunday: 'Sunday',
  sun: 'Sunday',
  monday: 'Monday',
  mon: 'Monday',
  tuesday: 'Tuesday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  wednesday: 'Wednesday',
  wed: 'Wednesday',
  weds: 'Wednesday',
  thursday: 'Thursday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  friday: 'Friday',
  fri: 'Friday',
  saturday: 'Saturday',
  sat: 'Saturday',
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function normalizePrintedWeekday(
  raw: string | null | undefined,
): PrintedWeekday | null {
  if (raw == null) return null;
  const key = raw.trim().toLowerCase().replace(/\.$/, '');
  if (!key) return null;
  return WEEKDAY_ALIASES[key] ?? null;
}

/** Weekday of a YYYY-MM-DD civil date. UTC calendar, not local midnight. */
export function weekdayOfIsoDate(iso: string): PrintedWeekday | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return PRINTED_WEEKDAYS[dt.getUTCDay()] ?? null;
}

export function formatReceiptDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return iso;
  return `${Number(m[3])} ${month} ${m[1]}`;
}

/**
 * Both dates when they differ: "receipt 1 Oct, bill 8 Oct".
 * The year is included when the two years differ.
 * Null until a bill date is stored, and null when the dates match.
 */
export function differingBillDatesLabel(
  receiptDate: unknown,
  billDate: unknown,
): string | null {
  const receipt = isoDateOnly(receiptDate);
  const bill = isoDateOnly(billDate);
  if (!receipt || !bill || receipt === bill) return null;
  const sameYear = receipt.slice(0, 4) === bill.slice(0, 4);
  const short = (iso: string): string => {
    const full = formatReceiptDate(iso);
    return sameYear ? full.replace(/ \d{4}$/, '') : full;
  };
  return `receipt ${short(receipt)}, bill ${short(bill)}`;
}

/** London civil today. A receipt dated today in the UK is not in the future. */
export function londonToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function isoDateOnly(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
  return m ? m[1]! : null;
}

/** The civil date twelve months before `iso`. 29 Feb clamps to 28 Feb. */
export function twelveMonthsBefore(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const y = Number(m[1]) - 1;
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCMonth() !== mo - 1) {
    return new Date(Date.UTC(y, mo, 0)).toISOString().slice(0, 10);
  }
  return dt.toISOString().slice(0, 10);
}

export function isMoreThanTwelveMonthsBefore(
  receiptDate: string,
  capturedOn: string,
): boolean {
  const limit = twelveMonthsBefore(capturedOn);
  if (!limit) return false;
  return receiptDate < limit;
}

export type DateFormParse =
  | { ok: true; weekday: PrintedWeekday | null; monthNamed: boolean }
  | { ok: false; message: string };

/**
 * The model must say whether a day name and a month name were printed.
 * Missing keys are a failed extraction, not a quiet "confident" date.
 */
export function parsePrintedDateForm(input: {
  weekday: string | null | undefined;
  monthNamed: boolean | null | undefined;
}): DateFormParse {
  if (input.weekday === undefined || typeof input.monthNamed !== 'boolean') {
    return {
      ok: false,
      message:
        'Date form was not reported (printed weekday, and whether a month name is printed)',
    };
  }
  if (input.weekday == null || input.weekday.trim() === '') {
    return { ok: true, weekday: null, monthNamed: input.monthNamed };
  }
  const weekday = normalizePrintedWeekday(input.weekday);
  if (!weekday) {
    return { ok: false, message: 'Unrecognised printed weekday' };
  }
  return { ok: true, weekday, monthNamed: input.monthNamed };
}

export type StoredDateFacts = {
  /** Both keys were stored by extraction. Absence is "not reported", not "digits only". */
  known: boolean;
  printedWeekday: PrintedWeekday | null;
  /** False only when extraction says the month was digits. Null = not a boolean fact. */
  monthNamed: boolean | null;
};

export function dateFactsFromSuggestion(suggested: unknown): StoredDateFacts {
  if (suggested == null || typeof suggested !== 'object' || Array.isArray(suggested)) {
    return { known: false, printedWeekday: null, monthNamed: null };
  }
  const row = suggested as Record<string, unknown>;
  if (!('receipt_date_weekday' in row) || !('receipt_date_month_named' in row)) {
    return { known: false, printedWeekday: null, monthNamed: null };
  }
  const month = row.receipt_date_month_named;
  return {
    known: true,
    printedWeekday: normalizePrintedWeekday(
      typeof row.receipt_date_weekday === 'string'
        ? row.receipt_date_weekday
        : null,
    ),
    monthNamed: typeof month === 'boolean' ? month : null,
  };
}

export type DateConfirmDecision =
  | { ok: true; acceptRecorded: boolean }
  | {
      ok: false;
      code: 'receipt_date_future' | 'receipt_date_weekday' | 'receipt_date_unsettled';
      message: string;
    };

/**
 * Weekday contradiction and a future date cannot be approved.
 * An ambiguous numeric date, or one more than twelve months before capture,
 * can be approved or typed.
 */
export function decideReceiptDateConfirm(input: {
  receiptDate: string;
  capturedOn: string | null;
  today: string;
  printedWeekday: PrintedWeekday | null;
  monthNamed: boolean | null;
  factsKnown: boolean;
  dateEdited: boolean;
  acceptUnsettled: boolean;
}): DateConfirmDecision {
  const weekday = weekdayOfIsoDate(input.receiptDate);
  if (!weekday) {
    return {
      ok: false,
      code: 'receipt_date_future',
      message: 'That receipt date is not a real calendar date.',
    };
  }
  if (input.printedWeekday && input.printedWeekday !== weekday) {
    return {
      ok: false,
      code: 'receipt_date_weekday',
      message: `The receipt prints ${input.printedWeekday}, but ${formatReceiptDate(input.receiptDate)} is a ${weekday}. Confirm is refused until the date matches the printed day.`,
    };
  }
  if (input.receiptDate > input.today) {
    return {
      ok: false,
      code: 'receipt_date_future',
      message: `${formatReceiptDate(input.receiptDate)} is after today. A purchase cannot be dated in the future.`,
    };
  }
  const ambiguous =
    input.factsKnown &&
    input.printedWeekday == null &&
    input.monthNamed === false;
  const aged =
    input.capturedOn != null &&
    isMoreThanTwelveMonthsBefore(input.receiptDate, input.capturedOn);
  if (ambiguous || aged) {
    if (input.dateEdited || input.acceptUnsettled) {
      return { ok: true, acceptRecorded: true };
    }
    const parts: string[] = [];
    if (ambiguous) {
      parts.push(
        'No weekday is printed and the date is digits only, so day and month can be swapped.',
      );
    }
    if (aged) {
      parts.push(
        `${formatReceiptDate(input.receiptDate)} is more than twelve months before this receipt was captured.`,
      );
    }
    return {
      ok: false,
      code: 'receipt_date_unsettled',
      message: `${parts.join(' ')} Approve this date or type it.`,
    };
  }
  return { ok: true, acceptRecorded: false };
}
